import type { LegalDocument } from "./types";

export const PRIVACY_POLICY: LegalDocument = {
  title: "Privacy Policy",
  subtitle: "AppearInAISearch — A Product of Sarucci Inc.",
  effectiveDate: "July 4, 2026",
  lastUpdated: "July 4, 2026",
  sections: [
    {
      id: "introduction",
      number: "1",
      title: "Introduction",
      blocks: [
        {
          kind: "paragraph",
          text: "Sarucci Inc. (“Company,” “we,” “us,” “our”), a Delaware corporation, operates the AppearInAISearch platform at appearinaisearch.com (the “Platform,” “Website,” or “Service”). This Privacy Policy (“Policy”) describes how we collect, use, store, share, and protect your personal data when you access or use our Platform.",
        },
        {
          kind: "paragraph",
          text: "This Privacy Policy applies to all users of the Platform, including hotel managers, hotel owners, marketing agencies, and other hospitality professionals who interact with our services worldwide.",
        },
        {
          kind: "paragraph",
          text: "By accessing or using the Platform, creating an Account, or submitting any personal data to us, you acknowledge that you have read and understood this Privacy Policy. Where required by applicable law, we will obtain your consent to the specific data practices described herein. If you do not agree with this Policy, please do not use the Platform.",
        },
        {
          kind: "paragraph",
          text: "This Privacy Policy should be read in conjunction with our Terms and Conditions, available at appearinaisearch.com/terms.",
        },
      ],
    },
    {
      id: "data-controller-and-roles",
      number: "2",
      title: "Data Controller and Roles",
      blocks: [
        {
          kind: "paragraph",
          text: "For the purposes of applicable data protection laws, including the General Data Protection Regulation (EU) 2016/679 (“EU GDPR”), the UK General Data Protection Regulation and the Data Protection Act 2018 (together, “UK GDPR”), the Swiss Federal Act on Data Protection (“FADP”), the California Consumer Privacy Act of 2018, as amended by the California Privacy Rights Act of 2020 (together, “CCPA/CPRA”), and other applicable privacy legislation, the data controller (or “business” under the CCPA/CPRA) responsible for your personal data is:",
        },
        {
          kind: "address",
          lines: [
            "Sarucci Inc.",
            "16192 Coastal Highway",
            "Lewes, Delaware 19958",
            "Sussex County, United States",
            "Email: support@sarucci.com",
          ],
        },
        {
          kind: "paragraph",
          lead: "Controller vs. Processor Role.",
          text: "With respect to Account, billing, usage, and Platform-improvement data, Sarucci Inc. acts as a data controller. To the extent we process personal data on behalf of and pursuant to the documented instructions of an enterprise or agency customer (for example, when an agency uploads properties on behalf of its hotel clients), we act as a data processor (or “service provider” under the CCPA/CPRA), and a separate data processing agreement will govern that relationship.",
        },
        {
          kind: "paragraph",
          lead: "EU/UK Representative.",
          text: "To the extent required under Article 27 of the EU GDPR or UK GDPR, we will designate a Representative in the European Union and the United Kingdom and publish their contact details on our Website. Until such time, EEA, UK, and Swiss data subjects may contact us directly at support@sarucci.com with the subject line “GDPR Inquiry.”",
        },
      ],
    },
    {
      id: "personal-data-we-collect",
      number: "3",
      title: "Personal Data We Collect",
      blocks: [
        { kind: "subheading", text: "3.1 Information You Provide Directly" },
        {
          kind: "list",
          items: [
            {
              term: "Account Registration Data:",
              text: "Name, email address, password (stored in hashed and salted form), company or organization name, job title or role.",
            },
            {
              term: "Hotel/Property Data:",
              text: "Hotel name, website URL(s), hotel location, property type, and other details you submit for analysis.",
            },
            {
              term: "Payment Data:",
              text: "Payment is processed by Stripe, Inc. We receive limited payment information from Stripe (e.g., last four digits of your card, card type, billing address, and transaction history). We do not store full credit card numbers, magnetic-stripe data, or CVV codes on our servers.",
            },
            {
              term: "Communication Data:",
              text: "Any information you provide when you contact us via email at support@sarucci.com, through support forms, or other communication channels, including your name, email address, and the content of your correspondence.",
            },
            {
              term: "Preferences and Settings:",
              text: "Your notification preferences, subscription plan selections, and Platform settings.",
            },
          ],
        },
        { kind: "subheading", text: "3.2 Information Collected Automatically" },
        {
          kind: "list",
          items: [
            {
              term: "Usage Data:",
              text: "Pages visited, features used, audit reports generated, search queries entered, clicks, time spent on pages, frequency of access, and interactions with Platform features.",
            },
            {
              term: "Device and Technical Data:",
              text: "IP address, browser type and version, operating system, device type, screen resolution, language preferences, and referring URL.",
            },
            {
              term: "Log Data:",
              text: "Server logs that record access times, pages viewed, error logs, and other diagnostic data.",
            },
            {
              term: "Cookie Data:",
              text: "Information collected through cookies and similar tracking technologies (see Section 8 below).",
            },
          ],
        },
        { kind: "subheading", text: "3.3 Information from Third Parties" },
        {
          kind: "list",
          items: [
            {
              term: "Stripe:",
              text: "Transaction confirmations, payment status, and billing details.",
            },
            {
              term: "AI Platforms:",
              text: "Publicly available search results and responses from ChatGPT, Gemini, Claude, and other AI platforms generated in response to test queries. This data relates to your hotel’s public presence and does not include personal data of hotel guests.",
            },
          ],
        },
        {
          kind: "subheading",
          text: "3.4 Categories of Personal Information (CCPA/CPRA Disclosure)",
        },
        {
          kind: "paragraph",
          text: "In the preceding twelve (12) months, we have collected the following statutory categories of personal information from Platform users, as defined in California Civil Code § 1798.140:",
        },
        {
          kind: "list",
          items: [
            "Identifiers (e.g., name, email address, IP address, account ID);",
            "Customer records information (e.g., billing address, payment card metadata received from Stripe);",
            "Commercial information (e.g., subscription plan, transaction history);",
            "Internet or other electronic network activity information (e.g., usage data, log data, device data);",
            "Geolocation data (approximate location derived from IP address only; we do not collect precise geolocation);",
            "Professional or employment-related information (e.g., job title, company name); and",
            "Inferences drawn from the above to create a user profile reflecting Platform usage patterns.",
          ],
        },
        {
          kind: "paragraph",
          text: "We collect this information from the sources described in Sections 3.1–3.3 and use it for the business purposes described in Section 4. The categories of third parties with whom we share personal information are described in Section 6.",
        },
        { kind: "subheading", text: "3.5 Sensitive Personal Information" },
        {
          kind: "paragraph",
          text: "We do not knowingly collect “sensitive personal information” as defined under the CCPA/CPRA or “special categories” of personal data as defined under Articles 9 and 10 of the EU/UK GDPR (such as government identifiers, precise geolocation, racial or ethnic origin, religious or philosophical beliefs, health, sex life, sexual orientation, genetic or biometric data, or criminal offenses). We ask that you do not submit such information to the Platform. If we become aware that we have inadvertently received such information, we will take reasonable steps to delete it.",
        },
        { kind: "subheading", text: "3.6 Data We Do NOT Collect" },
        { kind: "paragraph", text: "We want to be clear about what we do not collect:" },
        {
          kind: "list",
          items: [
            {
              term: "Hotel guest personal data:",
              text: "We do not collect, store, or process any personal information of your hotel’s guests (e.g., guest names, booking details, credit card information, stay history).",
            },
            {
              term: "Hotel operational data:",
              text: "We do not access your property management system (PMS), booking engine backend, or revenue management data.",
            },
          ],
        },
        {
          kind: "paragraph",
          text: "We only analyze publicly available website content and marketing data that you voluntarily submit.",
        },
      ],
    },
    {
      id: "how-we-use-your-personal-data",
      number: "4",
      title: "How We Use Your Personal Data",
      blocks: [
        { kind: "paragraph", text: "We use your personal data for the following purposes:" },
        { kind: "subheading", text: "4.1 Service Delivery" },
        {
          kind: "list",
          items: [
            "To create and manage your Account.",
            "To perform AI Audits of your hotel website across the five dimensions (AEO, GEO, AIO, Crawlability, SXO) and generate your LLM SEO Score.",
            "To conduct LLM Visibility Testing across ChatGPT, Gemini, Claude, and other AI platforms.",
            "To perform OTA vs Direct Booking Analysis comparing your direct presence to OTA presence in AI-generated answers.",
            "To run Persona Visibility testing across up to eight guest personas.",
            "To generate reports, scores, recommendations, schema suggestions, and content suggestions.",
            "To provide rank tracking and competitor comparison analytics.",
          ],
        },
        { kind: "subheading", text: "4.2 Payment Processing" },
        {
          kind: "list",
          items: [
            "To process payments for Paid Plans via Stripe.",
            "To manage your subscription, billing, and invoicing.",
            "To detect and prevent payment fraud.",
          ],
        },
        { kind: "subheading", text: "4.3 Communication" },
        {
          kind: "list",
          items: [
            "To send you transactional emails (e.g., account confirmation, password reset, payment receipts).",
            "To respond to your inquiries and support requests.",
            "To send you service updates, product announcements, and promotional communications (with your consent, where required by law, and subject to your right to opt out at any time).",
          ],
        },
        { kind: "subheading", text: "4.4 Platform Improvement" },
        {
          kind: "list",
          items: [
            "To analyze usage patterns and improve Platform features, performance, and user experience, using aggregated or de-identified data wherever practicable.",
            "To develop new features and services.",
            "To conduct internal research and analytics.",
            "To refine our scoring methodologies and audit frameworks.",
          ],
        },
        { kind: "subheading", text: "4.5 Legal and Compliance" },
        {
          kind: "list",
          items: [
            "To comply with applicable laws, regulations, and legal processes.",
            "To enforce our Terms and Conditions.",
            "To protect our rights, privacy, safety, or property, and that of our users or the public.",
            "To detect, prevent, or address fraud, security, or technical issues.",
          ],
        },
        { kind: "subheading", text: "4.6 Automated Processing and Scoring" },
        {
          kind: "paragraph",
          text: "The Platform uses automated processes to generate audit scores, LLM SEO Scores, visibility rankings, and recommendations. These outputs are advisory and analytical only and are not intended to produce legal or similarly significant effects on you within the meaning of Article 22 of the EU/UK GDPR. If you have questions about the logic, significance, or consequences of any automated analysis affecting you, or if you would like to request human review, please contact us at support@sarucci.com.",
        },
      ],
    },
    {
      id: "legal-bases-for-processing",
      number: "5",
      title: "Legal Bases for Processing (GDPR)",
      blocks: [
        {
          kind: "paragraph",
          text: "If you are located in the European Economic Area (EEA), the United Kingdom (UK), or Switzerland, we process your personal data based on the following legal grounds under the EU/UK GDPR and the Swiss FADP:",
        },
        {
          kind: "list",
          items: [
            {
              term: "Performance of a Contract (Article 6(1)(b)):",
              text: "Processing necessary to provide the Service, manage your Account, and fulfill our obligations under the Terms and Conditions.",
            },
            {
              term: "Legitimate Interests (Article 6(1)(f)):",
              text: "Processing necessary for our legitimate interests, including improving the Platform, conducting analytics, preventing fraud, and ensuring security — provided these interests are not overridden by your data protection rights and freedoms. You may request further information about our legitimate interests balancing test at support@sarucci.com.",
            },
            {
              term: "Consent (Article 6(1)(a)):",
              text: "Where we rely on your consent (e.g., for marketing communications or non-essential cookies), you have the right to withdraw consent at any time without affecting the lawfulness of processing based on consent before its withdrawal.",
            },
            {
              term: "Legal Obligation (Article 6(1)(c)):",
              text: "Processing necessary to comply with applicable legal obligations (e.g., tax reporting, responding to lawful requests from authorities).",
            },
          ],
        },
      ],
    },
    {
      id: "data-sharing-and-disclosure",
      number: "6",
      title: "Data Sharing and Disclosure",
      blocks: [
        {
          kind: "paragraph",
          text: "We do not sell or share your personal information for cross-context behavioral advertising, as those terms are defined under the CCPA/CPRA. We may share your data in the following limited circumstances:",
        },
        { kind: "subheading", text: "6.1 Service Providers" },
        {
          kind: "paragraph",
          text: "We share data with trusted third-party service providers who assist us in operating the Platform:",
        },
        {
          kind: "list",
          items: [
            {
              term: "Stripe, Inc. —",
              text: "Payment processing (receives payment and billing data).",
            },
            { term: "Cloud hosting providers —", text: "Infrastructure and data storage." },
            {
              term: "Analytics providers —",
              text: "Platform usage analytics (aggregated or de-identified where possible).",
            },
            {
              term: "Email service providers —",
              text: "Transactional and promotional email delivery.",
            },
          ],
        },
        {
          kind: "paragraph",
          text: "All service providers are contractually obligated to process your data only on our documented instructions, in accordance with applicable data protection laws, and only for the limited business purposes specified in our contracts. Under the CCPA/CPRA, disclosures to these service providers do not constitute a “sale” or “sharing” of personal information.",
        },
        { kind: "subheading", text: "6.2 AI Platforms" },
        {
          kind: "paragraph",
          text: "In the course of providing LLM Visibility Testing, we submit search queries (e.g., “luxury hotels in Paris, France”) to third-party AI platforms including ChatGPT (OpenAI), Gemini (Google), and Claude (Anthropic). These queries may contain your hotel name and location but do not include your Account credentials or personal data beyond what is necessary to test public visibility.",
        },
        { kind: "subheading", text: "6.3 Legal Requirements" },
        {
          kind: "paragraph",
          text: "We may disclose your data if required to do so by law or in the good-faith belief that such action is necessary to:",
        },
        {
          kind: "list",
          items: [
            "Comply with a legal obligation, court order, subpoena, or government request.",
            "Protect and defend our rights or property.",
            "Prevent or investigate possible wrongdoing in connection with the Platform.",
            "Protect the personal safety of users or the public.",
          ],
        },
        {
          kind: "paragraph",
          text: "Where legally permitted, we will use reasonable efforts to notify you of any such request before disclosure.",
        },
        { kind: "subheading", text: "6.4 Business Transfers" },
        {
          kind: "paragraph",
          text: "In the event of a merger, acquisition, reorganization, bankruptcy, or sale of all or a portion of our assets, your personal data may be transferred as part of such transaction. We will notify you via email or prominent notice on the Platform of any such change in ownership or control of your personal data. Any acquirer will be bound by privacy commitments no less protective than those described in this Policy, unless you consent otherwise.",
        },
        { kind: "subheading", text: "6.5 With Your Consent" },
        {
          kind: "paragraph",
          text: "We may share your data with other third parties when you have given us explicit consent to do so.",
        },
      ],
    },
    {
      id: "international-data-transfers",
      number: "7",
      title: "International Data Transfers",
      blocks: [
        {
          kind: "paragraph",
          text: "Sarucci Inc. is based in the United States. If you access the Platform from outside the United States, including from the EEA, UK, or Switzerland, your personal data will be transferred to and processed in the United States and other jurisdictions where we or our service providers operate. These jurisdictions may have data protection laws that differ from those of your country.",
        },
        {
          kind: "paragraph",
          text: "We rely on the following legal mechanisms to safeguard cross-border transfers of personal data:",
        },
        {
          kind: "list",
          items: [
            {
              term: "EEA transfers:",
              text: "The European Commission’s Standard Contractual Clauses (Decision (EU) 2021/914), together with a Transfer Impact Assessment and supplementary measures where required.",
            },
            {
              term: "UK transfers:",
              text: "The UK International Data Transfer Agreement (IDTA) or the UK Addendum to the EU Standard Contractual Clauses, as issued by the UK Information Commissioner’s Office.",
            },
            {
              term: "Swiss transfers:",
              text: "The EU Standard Contractual Clauses as adapted for use under the Swiss FADP, in accordance with guidance from the Swiss Federal Data Protection and Information Commissioner.",
            },
            {
              term: "Data Privacy Framework:",
              text: "Where applicable, transfers may be made in reliance on the EU–US, UK Extension, and Swiss–US Data Privacy Frameworks to certified recipients.",
            },
            "Technical and organizational security measures appropriate to the level of risk.",
            "Contractual obligations requiring our service providers to protect your data.",
          ],
        },
        {
          kind: "paragraph",
          text: "You may request a copy of the relevant transfer mechanism (with commercially sensitive information redacted) by contacting us at support@sarucci.com.",
        },
      ],
    },
    {
      id: "cookies-and-tracking",
      number: "8",
      title: "Cookies and Tracking Technologies",
      blocks: [
        {
          kind: "paragraph",
          text: "We use cookies and similar tracking technologies to collect and store information about your interactions with the Platform.",
        },
        { kind: "subheading", text: "8.1 Types of Cookies We Use" },
        {
          kind: "list",
          items: [
            {
              term: "Essential Cookies:",
              text: "Required for the Platform to function properly (e.g., session management, authentication, security). These cannot be disabled.",
            },
            {
              term: "Performance/Analytics Cookies:",
              text: "Help us understand how users interact with the Platform, which pages are visited most, and how we can improve performance.",
            },
            {
              term: "Functional Cookies:",
              text: "Remember your preferences and settings (e.g., language, notification preferences) to enhance your experience.",
            },
            {
              term: "Marketing/Advertising Cookies:",
              text: "Used to deliver relevant promotional content. These are only placed with your consent where required by law.",
            },
          ],
        },
        { kind: "subheading", text: "8.2 Managing Cookies" },
        {
          kind: "paragraph",
          text: "You can manage your cookie preferences through your browser settings and, where available, through our on-Platform cookie consent tool. Most browsers allow you to block or delete cookies; however, disabling essential cookies may impair Platform functionality. Where required by law (e.g., in the EEA, UK, and Switzerland), we will obtain your prior, informed, and revocable consent before placing non-essential cookies.",
        },
        { kind: "subheading", text: "8.3 Do Not Track and Global Privacy Control" },
        {
          kind: "paragraph",
          text: "Some browsers offer a “Do Not Track” (DNT) signal. There is no industry-standard response to DNT signals at this time, and we do not currently respond to DNT signals. However, in accordance with California law, we recognize and honor opt-out preference signals sent by the Global Privacy Control (GPC) or equivalent mechanisms as a valid request to opt out of the sale or sharing of personal information for browsers on which the signal is enabled.",
        },
      ],
    },
    {
      id: "data-retention",
      number: "9",
      title: "Data Retention",
      blocks: [
        {
          kind: "paragraph",
          text: "We retain your personal data only for as long as necessary to fulfill the purposes described in this Policy, or for such longer period as required by applicable law:",
        },
        {
          kind: "list",
          items: [
            {
              term: "Account Data:",
              text: "Retained for the duration of your Account and for up to twenty-four (24) months after Account deletion, unless a longer retention period is required by law or is necessary to establish, exercise, or defend legal claims.",
            },
            {
              term: "Audit and Report Data:",
              text: "Retained for the duration of your Account. Historical reports may be retained in de-identified or aggregated form for analytics and product improvement purposes.",
            },
            {
              term: "Payment Data:",
              text: "Transaction records are retained for a minimum of seven (7) years to comply with tax, accounting, and financial reporting obligations under U.S. federal and Delaware state law.",
            },
            {
              term: "Communication Data:",
              text: "Retained for up to thirty-six (36) months after the last communication.",
            },
            {
              term: "Log and Usage Data:",
              text: "Retained for up to twenty-four (24) months for security, analytics, and troubleshooting purposes.",
            },
          ],
        },
        {
          kind: "paragraph",
          text: "When personal data is no longer required, we will securely delete, destroy, or irreversibly de-identify it in accordance with our data retention procedures.",
        },
      ],
    },
    {
      id: "data-security",
      number: "10",
      title: "Data Security",
      blocks: [
        {
          kind: "paragraph",
          text: "We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction, including:",
        },
        {
          kind: "list",
          items: [
            "Encryption of data in transit (TLS 1.2 or higher) and at rest.",
            "Secure password hashing and salting.",
            "Multi-factor authentication for administrative access.",
            "Role-based access controls limiting data access to authorized personnel on a need-to-know basis.",
            "Regular security assessments, penetration testing, and vulnerability scanning.",
            "Secure cloud hosting infrastructure with industry-standard protections.",
            "Documented incident response procedures for data breach detection, containment, and notification.",
          ],
        },
        {
          kind: "paragraph",
          text: "While we implement industry-standard safeguards, no method of transmission over the Internet or electronic storage is entirely secure. We cannot guarantee absolute security, and you acknowledge that you provide data to us at your own risk to the extent permitted by applicable law.",
        },
      ],
    },
    {
      id: "your-rights",
      number: "11",
      title: "Your Rights",
      blocks: [
        { kind: "subheading", text: "11.1 Rights Available to All Users" },
        {
          kind: "paragraph",
          text: "Regardless of your location, and subject to applicable law and reasonable verification of your identity, you have the following rights:",
        },
        {
          kind: "list",
          items: [
            { term: "Access:", text: "Request a copy of the personal data we hold about you." },
            {
              term: "Correction:",
              text: "Request correction of inaccurate or incomplete personal data.",
            },
            {
              term: "Deletion:",
              text: "Request deletion of your personal data, subject to legal retention requirements and other lawful exceptions.",
            },
            {
              term: "Opt-Out of Marketing:",
              text: "Opt out of promotional communications at any time via the unsubscribe link or by contacting us.",
            },
            {
              term: "Account Deletion:",
              text: "Request deletion of your Account by contacting support@sarucci.com.",
            },
          ],
        },
        {
          kind: "subheading",
          text: "11.2 Additional Rights Under GDPR (EEA, UK, Switzerland Users)",
        },
        {
          kind: "paragraph",
          text: "If you are located in the EEA, UK, or Switzerland, you have the following additional rights under the EU/UK GDPR and the Swiss FADP:",
        },
        {
          kind: "list",
          items: [
            {
              term: "Right to Restriction of Processing:",
              text: "Request that we restrict the processing of your personal data in certain circumstances.",
            },
            {
              term: "Right to Data Portability:",
              text: "Receive your personal data in a structured, commonly used, and machine-readable format, and transmit it to another controller where technically feasible.",
            },
            {
              term: "Right to Object:",
              text: "Object to processing of your personal data based on legitimate interests, including profiling.",
            },
            {
              term: "Right to Withdraw Consent:",
              text: "Where processing is based on consent, withdraw your consent at any time without affecting the lawfulness of prior processing.",
            },
            {
              term: "Right in Relation to Automated Decisions:",
              text: "Where applicable, request human intervention, express your point of view, and contest decisions based solely on automated processing that produce legal or similarly significant effects on you.",
            },
            {
              term: "Right to Lodge a Complaint:",
              text: "File a complaint with your local data protection supervisory authority (in the UK, the Information Commissioner’s Office; in Switzerland, the Federal Data Protection and Information Commissioner).",
            },
          ],
        },
        {
          kind: "paragraph",
          text: "To exercise any of these rights, please contact us at support@sarucci.com. We will respond to your request within thirty (30) days, or within such longer period as permitted by applicable law where the request is complex or numerous.",
        },
        { kind: "subheading", text: "11.3 Rights Under CCPA/CPRA (California Residents)" },
        {
          kind: "paragraph",
          text: "If you are a California resident, you have the following rights under the CCPA/CPRA:",
        },
        {
          kind: "list",
          items: [
            {
              term: "Right to Know:",
              text: "Request disclosure of the categories and specific pieces of personal information we have collected about you, the sources of that information, the business or commercial purposes for collecting or sharing it, and the categories of third parties with whom we share it.",
            },
            {
              term: "Right to Delete:",
              text: "Request deletion of personal information we have collected from you, subject to statutory exceptions.",
            },
            {
              term: "Right to Correct:",
              text: "Request correction of inaccurate personal information we maintain about you.",
            },
            {
              term: "Right to Data Portability:",
              text: "Receive your personal information in a portable format.",
            },
            {
              term: "Right to Opt-Out of Sale or Sharing:",
              text: "We do not sell personal information and do not share it for cross-context behavioral advertising. If our practices change, we will provide a “Do Not Sell or Share My Personal Information” link on our Website and honor opt-out preference signals such as the Global Privacy Control.",
            },
            {
              term: "Right to Limit Use of Sensitive Personal Information:",
              text: "To the extent we process sensitive personal information for purposes other than those permitted by the CCPA/CPRA, you may direct us to limit its use. As noted in Section 3.5, we do not knowingly collect sensitive personal information.",
            },
            {
              term: "Right to Non-Discrimination:",
              text: "We will not discriminate against you for exercising your CCPA/CPRA rights, including by denying goods or services, charging different prices, or providing a different level of service.",
            },
            {
              term: "Authorized Agents:",
              text: "You may designate an authorized agent to submit requests on your behalf, subject to reasonable verification and, where applicable, written authorization.",
            },
          ],
        },
        {
          kind: "paragraph",
          text: "To exercise any of these rights, contact us at support@sarucci.com. We will verify your identity before responding to your request as required by law.",
        },
      ],
    },
    {
      id: "childrens-privacy",
      number: "12",
      title: "Children’s Privacy",
      blocks: [
        {
          kind: "paragraph",
          text: "The Platform is designed for business use by hospitality professionals and is not directed at children. We do not knowingly collect personal data from any individual under the age of sixteen (16), or such lower minimum age of digital consent as may apply in a particular EU Member State (but in no event under the age of thirteen (13), which is the minimum age under the U.S. Children’s Online Privacy Protection Act (“COPPA”)). If we become aware that we have inadvertently collected personal data from a child in violation of applicable law, we will take steps to delete such data promptly. If you are a parent or guardian and believe that a child has provided us with personal data, please contact us at support@sarucci.com.",
        },
      ],
    },
    {
      id: "third-party-links",
      number: "13",
      title: "Third-Party Links and Services",
      blocks: [
        {
          kind: "paragraph",
          text: "The Platform may contain links to third-party websites, services, or applications, including but not limited to:",
        },
        {
          kind: "list",
          items: [
            "Stripe (stripe.com) — Payment processing",
            "AI platforms (OpenAI, Google, Anthropic) — Referenced in visibility testing",
            "OTA platforms (Booking.com, Expedia, TripAdvisor) — Referenced in analyses",
          ],
        },
        {
          kind: "paragraph",
          text: "This Privacy Policy does not apply to third-party websites or services. We encourage you to review the privacy policies of any third-party service before providing personal data to them. We are not responsible for the privacy practices of third-party services.",
        },
      ],
    },
    {
      id: "data-breach-notification",
      number: "14",
      title: "Data Breach Notification",
      blocks: [
        {
          kind: "paragraph",
          text: "In the event of a personal data breach that is likely to result in a risk to your rights and freedoms, we will:",
        },
        {
          kind: "list",
          items: [
            "Notify the relevant supervisory authority within seventy-two (72) hours of becoming aware of the breach, where required by the EU/UK GDPR or other applicable law.",
            "Notify affected users without undue delay if the breach is likely to result in a high risk to their rights and freedoms.",
            "Comply with applicable U.S. state breach notification laws, which may require notification to affected residents and to state attorneys general or other regulators within specified timeframes.",
            "Document the breach, its effects, and the remedial actions taken.",
          ],
        },
      ],
    },
    {
      id: "changes-to-this-policy",
      number: "15",
      title: "Changes to This Privacy Policy",
      blocks: [
        {
          kind: "paragraph",
          text: "We may update this Privacy Policy from time to time to reflect changes in our practices, technologies, legal requirements, or other factors. When we make material changes:",
        },
        {
          kind: "list",
          items: [
            "We will update the “Last Updated” date at the top of this Policy.",
            "We will notify you by email or through a prominent notice on the Platform at least thirty (30) days before the changes take effect, where reasonably practicable.",
            "For material changes that require consent under applicable law, we will obtain your consent before implementing the changes.",
          ],
        },
        {
          kind: "paragraph",
          text: "We encourage you to review this Policy periodically. Your continued use of the Platform after any changes constitutes your acceptance of the updated Policy to the extent permitted by applicable law.",
        },
      ],
    },
    {
      id: "contact-us",
      number: "16",
      title: "Contact Us",
      blocks: [
        {
          kind: "paragraph",
          text: "If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us at:",
        },
        {
          kind: "address",
          lines: [
            "Sarucci Inc.",
            "AppearInAISearch",
            "16192 Coastal Highway",
            "Lewes, Delaware 19958",
            "Sussex County, United States",
            "Email: support@sarucci.com",
            "Website: appearinaisearch.com",
          ],
        },
        {
          kind: "paragraph",
          text: "For GDPR-related inquiries from the EEA, UK, or Switzerland, please direct your communication to the same email address with the subject line “GDPR Inquiry.” For CCPA/CPRA requests, please use the subject line “California Privacy Request.”",
        },
      ],
    },
  ],
};
